import React, { useState } from 'react'

import { Modal, Button, Select, Col, Row, Typography, Tag, Card, Statistic, ModalProps, Icons, useTable, Space, Table } from '@pankod/refine-antd'
import evaluationRequestMetaData from 'metadata/evaluationRequest'
import { IEvaluationRequests, IQuestion, IPaper } from 'interfaces'
import { exportToExcel } from 'exportToExcel'

type Props = {
  modalProps: ModalProps
  close: any
  show: any
  questionList: IQuestion[] | undefined
  paper: IPaper | undefined
}

type exportProps = {
  id: string
  question_id: string
  title: string
  question_type: string
  paper: string
  discrimination_index: string
}

const { ExportOutlined } = Icons
const { Text, Title } = Typography

export const DiscriminationIndexFromPaper = ({ modalProps, close, show, questionList, paper }: Props) => {
  const ruleDefined = 10
  const [selectedEvaluationRequest, setSelectedEvaluationRequest] = useState<IEvaluationRequests | undefined>(undefined)
  const [exportData, setExportData] = useState<exportProps[]>([])

  const { tableQueryResult: evaluationRequestQueryResult } = useTable<IEvaluationRequests>({
    resource: 'evaluation_requests',
    metaData: evaluationRequestMetaData,
    permanentFilter: [{ field: 'status', operator: 'eq', value: 'Score Finalised' }],
    initialSorter: [{ field: 'request_id', order: 'desc' }],
    queryOptions: { enabled: !!modalProps?.visible },
  })

  const getDITagColor = (diValue: string) => {
    let color: string = 'geekblue'
    let di = Math.abs(Number(diValue))
    if (di < 0.25) {
      color = 'red'
    } else if (di >= 0.25 && di < 0.5) {
      color = 'gold'
    } else if (di >= 0.5 && di < 0.75) {
      color = 'lime'
    } else color = 'green'
    return color
  }

  const handleExport = () => {
    exportToExcel(exportData, `paper_#${paper?.paper_id}_DI_${Math.round(new Date().getTime() / 1000)}.xlsx`)
  }

  const handletGetDI = () => {
    const evaluationList = selectedEvaluationRequest?.evaluations
    let allCandidateList: any[] = []
    evaluationList?.forEach(evaluation => {
      allCandidateList.push(...evaluation?.evaluation_candidates)
    })

    allCandidateList = allCandidateList.sort((candidateOne, candidateTwo) => {
      return candidateTwo?.total_score - candidateOne?.total_score
    })

    const exportDataArr: exportProps[] = []
    const requirement = Math.round((ruleDefined / 100) * allCandidateList?.length)

    paper?.questionAnswerKeys?.forEach((data: any) => {
      const questionKey = data?.number
      const questionId = data?.id
      let questionTitle: string = ''
      let questionType: string = ''
      let questionDatabaseNumber = ''

      let totalScoreByAll = 0
      let topScoreTotal = 0
      let bottomScoreTotal = 0

      for (let i = 0; i < requirement; i++) {
        topScoreTotal += Number(allCandidateList[i]?.answers?.[Number(questionKey) - 1]?.mark_scored)
        const lastCandidate = allCandidateList?.slice(-i - 1)?.[0]

        bottomScoreTotal += lastCandidate?.answers?.[Number(questionKey) - 1]?.mark_scored ?? 0

        totalScoreByAll += allCandidateList[i]?.answers?.[Number(questionKey) - 1]?.mark + lastCandidate?.answers?.[Number(questionKey) - 1]?.mark
      }

      const DI = Math.round(((topScoreTotal - bottomScoreTotal) / totalScoreByAll + Number.EPSILON) * 100) / 100
      questionList?.forEach(question => {
        if (question?.id === questionId) {
          questionType = question?.question_type_id
          questionTitle = question?.title
          questionDatabaseNumber = `${question?.question_id}`
        }
      })

      exportDataArr.push({
        id: questionId,
        question_id: questionDatabaseNumber,
        title: questionTitle,
        question_type: questionType,
        discrimination_index: `${DI}`,
        paper: `#${paper?.paper_id} ${paper?.paper_smart_id}`,
      })
    })

    setExportData(exportDataArr)
  }

  return (
    <>
      <Button icon={<ExportOutlined />} onClick={() => show()}>
        Export DI
      </Button>
      <Modal
        {...modalProps}
        title="Export Discrimination Index of All Questions"
        width={900}
        footer={
          <Space>
            <Button
              onClick={() => {
                close()
              }}
            >
              Close
            </Button>
            <Button
              onClick={() => {
                handleExport()
              }}
            >
              Export
            </Button>
          </Space>
        }
      >
        <Row gutter={[16, 48]}>
          <Col span={12}>
            <Select
              style={{ width: '100%' }}
              placeholder="Select an evaluation"
              onChange={value => {
                const data = JSON.parse(value)
                setSelectedEvaluationRequest(data)
              }}
            >
              {evaluationRequestQueryResult?.data?.data?.map((evaluationRequest, idx) => (
                <option key={idx} value={JSON.stringify(evaluationRequest)}>
                  #{evaluationRequest?.request_id} {evaluationRequest?.name}
                </option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Button
              disabled={!!!selectedEvaluationRequest}
              onClick={() => {
                handletGetDI()
              }}
            >
              Get DI
            </Button>
          </Col>
          <Table dataSource={exportData} bordered showHeader>
            <Table.Column title="question uuid" dataIndex="id" render={value => <Text>{value}</Text>} />
            <Table.Column
              title="question id"
              dataIndex="question_id"
              render={value => (
                <Text strong code>
                  #{value}
                </Text>
              )}
            />
            <Table.Column title="Question Title" dataIndex="title" render={value => <Text ellipsis={{ tooltip: value }}>{value}</Text>} />
            <Table.Column
              title="Discrimination Index"
              dataIndex="discrimination_index"
              render={value => <Tag color={getDITagColor(value)}>{value}</Tag>}
            />
            <Table.Column title="Paper" dataIndex="paper" render={value => <Text>{value}</Text>} />
          </Table>
        </Row>
      </Modal>
    </>
  )
}
